import React from "react";
import { Tabs, Tab } from "@mui/material";

/**
 * Tab geometry/type baked into BaseTabs, straight from the design CSS:
 *   container .Wrapper-Tab / .Tab — 35px tall, centered, equal-width (flex-grow)
 *   text .Headline — Inter 14/500, line-height 1.57, centered.
 * Colors are the colors_v2 `tabs-functional-*` tokens so tabs stay theme-driven.
 */
const TAB = {
  height: 35,
  radius: "6px",
  font: "Inter",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.57,
  gap: "6px",
  padding: "8px 16px",
  // .Tab (active) shadow — top/bottom only, no side bleed.
  // The design CSS referenced a `--10` token (Figma export shorthand for
  // "black @ 10% opacity") which doesn't exist in this app, so the whole
  // shadow was silently dropped; use the literal rgba it stood for.
  // To keep the shadow off the sides we give each layer a NEGATIVE horizontal
  // spread equal to its blur radius: the spread shrinks the shadow box inward
  // on every edge, the vertical offset pushes it back out top/bottom, and the
  // net result is a shadow that projects up/down but is pulled flush to the
  // sides. No clip-path needed (clip-path was cutting the shadow entirely).
  activeShadow:
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)"
};

/**
 * BaseTabs — the canonical reskin tab strip.
 *
 * WHAT IT IS: an equal-width horizontal tab strip matching the design's
 * `.Wrapper-Tab` / `.Tab` spec — a rounded neutral bar where the active tab
 * gets the lighter active fill plus a soft shadow, inactive tabs use the
 * inactive fill and lighten on hover. Replaces the bespoke MUI `Tabs`/`Tab`
 * `sx` block (indicator bar) at call sites.
 *
 * WHAT IT WRAPS: MUI `Tabs` + `Tab`. The MUI indicator bar is hidden — the
 * active state is shown by the tab's own background/shadow instead.
 *
 * CONTRACT (props in):
 *  - value: index of the active tab (controlled).
 *  - onChange: (event, newIndex) => void — forwarded from MUI Tabs.
 *  - tabs: [{ label, value?, disabled?, testId? }] — one entry per tab. `label`
 *      is any node (e.g. <FormattedMessage/>). `value` defaults to the array
 *      index. `testId` becomes the tab's data-testid.
 *  - "aria-label": forwarded to the Tabs container.
 *  - sx: merged OVER the container defaults so per-call overrides win.
 *  - ...props: spread onto the MUI Tabs container.
 *
 * BEHAVIOR (out): a styled, controlled tab strip. Tabs share the row width
 * equally (flex: 1) and never wrap.
 *
 * data-testid: not derived — pass `testId` per tab; it is forwarded verbatim so
 * existing selectors (e.g. "user-info-tab") keep working.
 */

// Rounded container holding the whole strip. The active/inactive fills live on
// the tabs themselves; this just frames them and clips the corner radius.
const containerSx = {
  minHeight: TAB.height,
  borderRadius: TAB.radius,
  // No overflow clipping here: overflow:hidden swallows the active tab's shadow
  // on all sides. The active tab clips its OWN shadow to top/bottom via
  // clip-path (see tabSx), so the container can stay visible.
  backgroundColor: "var(--tabs-functional-inactive-background-neutral)",
  // The active state is the tab background + shadow, not an underline bar.
  ".MuiTabs-indicator": { display: "none" },
  // Tabs sit flush against each other. MUI's flexContainer has no gap by
  // default, but override explicitly so a theme default can't reintroduce one.
  // It also carries the 6px radius so the strip's corners render rounded.
  ".MuiTabs-flexContainer": { gap: 0, borderRadius: TAB.radius }
};

const tabSx = {
  flex: 1,
  maxWidth: "none",
  minHeight: TAB.height,
  height: TAB.height,
  // Row layout with the .Wrapper-Tab gap between an (optional) icon and text.
  flexDirection: "row",
  gap: TAB.gap,
  padding: TAB.padding,
  fontFamily: TAB.font,
  fontSize: TAB.fontSize,
  fontWeight: TAB.fontWeight,
  lineHeight: TAB.lineHeight,
  textTransform: "none",
  textAlign: "center",
  // .Wrapper-Tab (inactive)
  backgroundColor: "var(--tabs-functional-inactive-background-neutral)",
  color: "var(--tabs-functional-inactive-content-neutral)",
  "&:hover": {
    backgroundColor: "var(--tabs-functional-inactive-background-hover)"
  },
  // .Tab (active) — lighter fill, active content color, soft shadow.
  "&.Mui-selected": {
    backgroundColor: "var(--tabs-functional-active-background-neutral)",
    color: "var(--tabs-functional-active-content-neutral)",
    boxShadow: TAB.activeShadow,
    // Raise the active tab above its neighbours so its shadow paints over them
    // instead of being covered by the next tab's background.
    position: "relative",
    zIndex: 1
  },
  // Round only the outer corners of the strip so the edge tabs follow the
  // container radius; inner edges stay square where tabs meet.
  "&:first-of-type": {
    borderTopLeftRadius: TAB.radius,
    borderBottomLeftRadius: TAB.radius
  },
  "&:last-of-type": {
    borderTopRightRadius: TAB.radius,
    borderBottomRightRadius: TAB.radius
  }
};

const BaseTabs = React.forwardRef(
  ({ value, onChange, tabs = [], sx, ...props }, ref) => {
    return (
      <Tabs
        ref={ref}
        value={value}
        onChange={onChange}
        variant="fullWidth"
        // Remove the sliding indicator bar entirely — it doesn't suit the
        // filled/shadowed active-tab design.
        TabIndicatorProps={{ sx: { display: "none" } }}
        // Caller sx spreads last so per-call overrides win over the defaults.
        sx={{ ...containerSx, ...sx }}
        {...props}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.value ?? index}
            label={tab.label}
            value={tab.value ?? index}
            disabled={tab.disabled}
            data-testid={tab.testId}
            disableRipple
            sx={tabSx}
          />
        ))}
      </Tabs>
    );
  }
);

BaseTabs.displayName = "BaseTabs";

export default BaseTabs;
